import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Stack,
  Alert,
  FormControl,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { Course, CourseWithFlows, CourseFlow, Flow } from '../../types';

const CourseManagement: React.FC = () => {
  const [courses, setCourses] = useState<CourseWithFlows[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [addFlowDialogOpen, setAddFlowDialogOpen] = useState<boolean>(false);
  const [selectedCourse, setSelectedCourse] = useState<CourseWithFlows | null>(null);
  const [availableFlows, setAvailableFlows] = useState<Flow[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<number | null>(null);
  const [isRequired, setIsRequired] = useState<boolean>(true);

  useEffect(() => {
    fetchCourses();
    fetchFlows();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/courses/');
      setCourses(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError('Failed to fetch courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFlows = async () => {
    try {
      const response = await api.get('/api/flows/');
      console.log('Available flows:', response.data);
      setAvailableFlows(response.data);
    } catch (err) {
      console.error('Error fetching flows:', err);
    }
  };

  const fetchCourseDetails = async (courseId: number) => {
    try {
      const response = await api.get(`/api/courses/${courseId}/`);
      console.log('Course details:', response.data);
      
      // Update the course in the courses array
      setCourses(prevCourses => {
        return prevCourses.map(course => {
          if (course.id === courseId) {
            return response.data;
          }
          return course;
        });
      });
      
      // If this is the selected course, update it
      if (selectedCourse && selectedCourse.id === courseId) {
        setSelectedCourse(response.data);
      }
      
      return response.data;
    } catch (err) {
      console.error('Error fetching course details:', err);
      return null;
    }
  };

  const handleCreateCourse = async () => {
    if (!selectedCourse) return;

    try {
      await api.post('/api/courses/', {
        name: selectedCourse.name,
        description: selectedCourse.description,
        is_active: selectedCourse.is_active
      });
      setCreateDialogOpen(false);
      setSelectedCourse(null);
      fetchCourses();
    } catch (err) {
      console.error('Error creating course:', err);
      setError('Failed to create course. Please try again.');
    }
  };

  const handleUpdateCourse = async () => {
    if (!selectedCourse) return;

    try {
      await api.put(`/api/courses/${selectedCourse.id}/`, {
        name: selectedCourse.name,
        description: selectedCourse.description,
        is_active: selectedCourse.is_active
      });
      setEditDialogOpen(false);
      setSelectedCourse(null);
      fetchCourses();
    } catch (err) {
      console.error('Error updating course:', err);
      setError('Failed to update course. Please try again.');
    }
  };

  const handleDeleteCourse = async () => {
    if (!selectedCourse) return;

    try {
      await api.delete(`/api/courses/${selectedCourse.id}/`);
      setDeleteDialogOpen(false);
      setSelectedCourse(null);
      fetchCourses();
    } catch (err) {
      console.error('Error deleting course:', err);
      setError('Failed to delete course. Please try again.');
    }
  };

  const handleAddFlow = async () => {
    if (!selectedCourse || !selectedFlow) return;

    try {
      await api.post(`/api/courses/${selectedCourse.id}/add_flow/`, {
        flow_id: selectedFlow,
        is_required: isRequired
      });
      
      // Fetch updated course details to refresh the flows list
      await fetchCourseDetails(selectedCourse.id);
      
      setAddFlowDialogOpen(false);
      setSelectedFlow(null);
      setIsRequired(true);
    } catch (err) {
      console.error('Error adding flow to course:', err);
      setError('Failed to add flow to course. Please try again.');
    }
  };

  const handleRemoveFlow = async (courseId: number, flowId: number) => {
    try {
      await api.post(`/api/courses/${courseId}/remove_flow/`, {
        course_flow_id: flowId
      });
      fetchCourses();
    } catch (err) {
      console.error('Error removing flow from course:', err);
      setError('Failed to remove flow from course. Please try again.');
    }
  };

  const handleMoveFlow = async (courseId: number, flowId: number, direction: 'up' | 'down') => {
    try {
      const course = courses.find(c => c.id === courseId);
      if (!course || !course.course_flows) return;

      const courseFlow = course.course_flows.find(cf => cf.id === flowId);
      if (!courseFlow) return;

      const currentIndex = course.course_flows.indexOf(courseFlow);
      let newIndex = currentIndex;

      if (direction === 'up' && currentIndex > 0) {
        newIndex = currentIndex - 1;
      } else if (direction === 'down' && currentIndex < course.course_flows.length - 1) {
        newIndex = currentIndex + 1;
      } else {
        return; // No change needed
      }

      // Create a new array of flow IDs in the desired order
      const flowOrders = course.course_flows.map((cf, index) => ({
        id: cf.id,
        order: index + 1
      }));
      
      // Swap the orders of the two affected flows
      [flowOrders[currentIndex].order, flowOrders[newIndex].order] = 
      [flowOrders[newIndex].order, flowOrders[currentIndex].order];

      await api.post(`/api/courses/${courseId}/reorder_flows/`, {
        flow_orders: flowOrders
      });
      fetchCourses();
    } catch (err) {
      console.error('Error reordering flows:', err);
      setError('Failed to reorder flows. Please try again.');
    }
  };

  const handleCourseClick = (course: CourseWithFlows) => {
    // Fetch the detailed course information including flows
    fetchCourseDetails(course.id);
    setSelectedCourse(course);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Course Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedCourse({
              id: 0,
              name: '',
              description: '',
              is_active: true,
              created_at: '',
              updated_at: '',
              course_flows: []
            });
            setCreateDialogOpen(true);
          }}
          sx={{ mb: 2 }}
        >
          Create New Course
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : courses.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">No courses found. Create your first course!</Typography>
          </Paper>
        ) : (
          <Box>
            {courses.map((course) => (
              <Accordion key={course.id} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={() => handleCourseClick(course)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                    <Typography variant="h6">{course.name}</Typography>
                    <Chip
                      label={course.is_active ? 'Active' : 'Inactive'}
                      color={course.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" paragraph>
                    {course.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">Flows in this course:</Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setSelectedCourse(course);
                        setAddFlowDialogOpen(true);
                      }}
                    >
                      Add Flow
                    </Button>
                  </Box>
                  {!course.course_flows || course.course_flows.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No flows added to this course yet.
                    </Typography>
                  ) : (
                    <List>
                      {course.course_flows.map((courseFlow) => (
                        <ListItem
                          key={courseFlow.id}
                          divider
                        >
                          <ListItemText
                            primary={courseFlow.flow_details.name}
                            secondary={
                              <>
                                <Typography variant="body2" component="span">
                                  {courseFlow.flow_details.description}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={courseFlow.is_required ? 'Required' : 'Optional'}
                                  color={courseFlow.is_required ? 'primary' : 'default'}
                                  sx={{ ml: 1 }}
                                />
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Stack direction="row" spacing={1}>
                              <IconButton
                                edge="end"
                                onClick={() => handleMoveFlow(course.id, courseFlow.id, 'up')}
                                disabled={course.course_flows.indexOf(courseFlow) === 0}
                              >
                                <ArrowUpwardIcon />
                              </IconButton>
                              <IconButton
                                edge="end"
                                onClick={() => handleMoveFlow(course.id, courseFlow.id, 'down')}
                                disabled={course.course_flows.indexOf(courseFlow) === course.course_flows.length - 1}
                              >
                                <ArrowDownwardIcon />
                              </IconButton>
                              <IconButton
                                edge="end"
                                onClick={() => handleRemoveFlow(course.id, courseFlow.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        setSelectedCourse(course);
                        setEditDialogOpen(true);
                      }}
                      sx={{ mr: 1 }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setSelectedCourse(course);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Box>

      {/* Create Course Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Course</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Course Name"
            fullWidth
            value={selectedCourse?.name || ''}
            onChange={(e) => setSelectedCourse(prev => prev ? { ...prev, name: e.target.value } : null)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={selectedCourse?.description || ''}
            onChange={(e) => setSelectedCourse(prev => prev ? { ...prev, description: e.target.value } : null)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={selectedCourse?.is_active || false}
                onChange={(e) => setSelectedCourse(prev => prev ? { ...prev, is_active: e.target.checked } : null)}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateCourse} variant="contained" color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Course Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Course</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Course Name"
            fullWidth
            value={selectedCourse?.name || ''}
            onChange={(e) => setSelectedCourse(prev => prev ? { ...prev, name: e.target.value } : null)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={selectedCourse?.description || ''}
            onChange={(e) => setSelectedCourse(prev => prev ? { ...prev, description: e.target.value } : null)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={selectedCourse?.is_active || false}
                onChange={(e) => setSelectedCourse(prev => prev ? { ...prev, is_active: e.target.checked } : null)}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateCourse} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Course Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Course</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this course?</Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteCourse} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Flow Dialog */}
      <Dialog open={addFlowDialogOpen} onClose={() => setAddFlowDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Flow to Course</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <TextField
              select
              label="Select Flow"
              value={selectedFlow || ''}
              onChange={(e) => setSelectedFlow(Number(e.target.value))}
              SelectProps={{
                native: true,
              }}
            >
              <option value="">Select a flow</option>
              {availableFlows.map((flow) => (
                <option key={flow.id} value={flow.id}>
                  {flow.name}
                </option>
              ))}
            </TextField>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
              />
            }
            label="Required"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFlowDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddFlow}
            variant="contained"
            color="primary"
            disabled={selectedFlow === null}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CourseManagement;
